import React, { useMemo, useState } from "react";
import { fmtMoney, fmtNumber } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Gauge } from "lucide-react";
import HireUsCTA from "@/components/HireUsCTA";
import EmailResultsCard from "@/components/EmailResultsCard";
import NudgeCard from "@/components/NudgeCard";

// NZ 2025 progressive PAYE brackets (individual, excludes ACC levy).
const BRACKETS = [
  { cap: 15600, rate: 0.105 },
  { cap: 53500, rate: 0.175 },
  { cap: 78100, rate: 0.3 },
  { cap: 180000, rate: 0.33 },
  { cap: Infinity, rate: 0.39 },
];

function compute(income) {
  // IRD rule: income is rounded DOWN to the nearest dollar before tax is
  // calculated. Without this, every income with cents over-estimates the
  // tax by a few cents (e.g. $203,267.82 → 32c too high).
  const gross = Number(income) || 0;
  const taxable = Math.floor(gross);
  let remaining = Math.max(0, taxable);
  let prev = 0;
  let tax = 0;
  const breakdown = [];
  for (const { cap, rate } of BRACKETS) {
    const band = Math.min(remaining, cap - prev);
    if (band <= 0) break;
    const t = band * rate;
    tax += t;
    breakdown.push({
      band:
        prev === 0
          ? `$0 – $${cap.toLocaleString()}`
          : cap === Infinity
            ? `$${(prev + 1).toLocaleString()}+`
            : `$${(prev + 1).toLocaleString()} – $${cap.toLocaleString()}`,
      rate,
      amount: band,
      tax: t,
    });
    remaining -= band;
    prev = cap;
    if (remaining <= 0) break;
  }
  // Round to nearest cent for display, matching IRD's published precision.
  tax = Math.round(tax * 100) / 100;
  return {
    gross,
    tax,
    net: gross - tax,
    effective: gross > 0 ? (tax / gross) * 100 : 0,
    breakdown,
  };
}

export default function TaxCalc() {
  const { t } = useLang();
  const [income, setIncome] = useState(80000);
  const [freq, setFreq] = useState("annual"); // annual | weekly | fortnightly | monthly
  const annual = useMemo(() => {
    const n = Number(income) || 0;
    switch (freq) {
      case "weekly":
        return n * 52;
      case "fortnightly":
        return n * 26;
      case "monthly":
        return n * 12;
      default:
        return n;
    }
  }, [income, freq]);

  const r = compute(annual);
  const perWeek = r.net / 52;

  return (
    <div className="stagger-in">
      <div className="label-mono">/ {t("common.calculator", "CALCULATOR")} · 04</div>
      <h1 className="font-display text-5xl tracking-tight mt-2">{t("tax.h1", "NZ Tax / PAYE")}</h1>
      <p className="text-zinc-400 mt-2">
        {t("tax.sub", "New progressive brackets effective 1 April 2025. Sole traders — set this aside so IRD doesn't come knocking.")}
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 mt-10 border border-[var(--border)]">
        <div className="lg:col-span-3 p-4 sm:p-6 md:p-8 border-r border-[var(--border)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="label-mono mb-2">Income amount</div>
              <input
                type="number"
                step="100"
                className="input-sharp text-2xl py-4"
                value={income}
                onChange={(e) => setIncome(e.target.value)}
                data-testid="tax-income-input"
              />
            </div>
            <div>
              <div className="label-mono mb-2">Frequency</div>
              <select
                className="input-sharp text-sm py-4"
                value={freq}
                onChange={(e) => setFreq(e.target.value)}
                data-testid="tax-freq-select"
              >
                <option value="annual">Per year</option>
                <option value="monthly">Per month</option>
                <option value="fortnightly">Per fortnight</option>
                <option value="weekly">Per week</option>
              </select>
            </div>
          </div>

          <div className="mt-8">
            <div className="flex items-baseline justify-between gap-3 mb-3">
              <div className="label-mono">Bracket breakdown</div>
              <div className="text-[10px] font-mono text-[var(--primary)] uppercase tracking-wider">
                Effective 1 Apr 2025
              </div>
            </div>
            <div className="border border-[var(--border)] overflow-x-auto">
              <div className="min-w-[480px]">
              <div className="grid grid-cols-12 gap-0 text-xs font-mono uppercase tracking-wider text-zinc-500 border-b border-[var(--border)]">
                <div className="col-span-5 p-3">Band</div>
                <div className="col-span-2 p-3 border-l border-[var(--border)]">Rate</div>
                <div className="col-span-3 p-3 border-l border-[var(--border)]">Taxed on</div>
                <div className="col-span-2 p-3 border-l border-[var(--border)]">Tax</div>
              </div>
              {r.breakdown.map((b, i) => (
                <div
                  key={i}
                  className="grid grid-cols-12 gap-0 text-sm font-mono border-b border-[var(--border)] last:border-b-0"
                  data-testid={`tax-band-${i}`}
                >
                  <div className="col-span-5 p-3">{b.band}</div>
                  <div className="col-span-2 p-3 border-l border-[var(--border)] text-[var(--primary)]">
                    {(b.rate * 100).toFixed(1)}%
                  </div>
                  <div className="col-span-3 p-3 border-l border-[var(--border)]">
                    {fmtMoney(b.amount)}
                  </div>
                  <div className="col-span-2 p-3 border-l border-[var(--border)]">
                    {fmtMoney(b.tax)}
                  </div>
                </div>
              ))}
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 p-4 sm:p-6 md:p-8 bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <Gauge size={16} className="text-[var(--primary)]" />
            <div className="label-mono">Your take-home</div>
          </div>
          <div className="mt-4 space-y-3 font-mono">
            <Row label="Gross (annual)" value={fmtMoney(r.gross)} testid="tax-gross" />
            <Row label="Tax" value={fmtMoney(r.tax)} testid="tax-total" accent />
            <Row label="Effective rate" value={`${fmtNumber(r.effective, 2)}%`} testid="tax-eff" />
            <div className="border-t border-[var(--border)] pt-3">
              <Row label="Net (annual)" value={fmtMoney(r.net)} big testid="tax-net" />
              <Row label="~Weekly" value={fmtMoney(perWeek)} />
            </div>
          </div>
          <div className="hi-vis-stripe h-1.5 mt-8 -mx-4 sm:-mx-6 md:-mx-8 -mb-4 sm:-mb-6 md:-mb-8" />
        </div>
      </div>

      <div className="text-xs font-mono text-zinc-500 mt-6 max-w-2xl">
        Indicative only. Does not include ACC earner's levy, student loan,
        KiwiSaver or other deductions. Always confirm with IRD or your accountant.
      </div>

      <NudgeCard
        show={annual >= 80000}
        tone="info"
        title={annual >= 180000 ? "Top tax bracket — 39%" : "30%+ tax bracket"}
        body={
          annual >= 180000
            ? "Every $100 you earn over $180k is taxed at 39%. Smart tax planning is where an accountant pays for themselves several times over. We do this for company directors and high-earning sole traders every day."
            : "You're now paying 30%+ on each extra dollar. Provisional tax kicks in next year — getting ahead of it now saves penalties later."
        }
        cta="Talk to a tax agent"
        campaign="tax-calc-high-bracket"
      />

      <NudgeCard
        show={annual > 0 && annual < 80000}
        tone="warning"
        title="Set this aside — don't spend it"
        body={`On $${Math.round(annual).toLocaleString()} you owe IRD about ${fmtMoney(r.tax)} this year. We email a monthly reminder so you actually have it when IRD asks.`}
        cta="Get monthly tax emails"
        campaign="tax-calc-set-aside"
      />

      <EmailResultsCard
        calculator="tax-paye"
        payload={{
          gross_annual: r.gross,
          tax: r.tax,
          net: r.net,
          effective_rate: r.effective,
          frequency: freq,
          input_amount: Number(income) || 0,
        }}
        labelKey="email.tax.label"
        descKey="email.tax.desc"
      />

      <HireUsCTA campaign="tax-calc" />
    </div>
  );
}

function Row({ label, value, big, accent, testid }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="label-mono">{label}</span>
      <span
        data-testid={testid}
        className={`${big ? "text-3xl" : "text-base"} ${
          accent ? "text-[var(--primary)]" : "text-white"
        }`}
      >
        {value}
      </span>
    </div>
  );
}
